import React, { useState, useEffect } from 'react';
import { Search, MessageSquare, CheckCircle, Clock, X, Send } from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const SupportCenter = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${constant.API_BASE_URL}/admin/business/support/tickets`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (response.data.success) {
        setTickets(response.data.data);
        if (selectedTicket) {
          const updated = response.data.data.find(t => t.id === selectedTicket.id);
          setSelectedTicket(updated || null);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    setReplyLoading(true);
    try {
      const res = await axios.post(`${constant.API_BASE_URL}/admin/business/support/tickets/${selectedTicket.id}/reply`, {
        message: replyText
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        setReplyText('');
        fetchTickets();
      }
    } catch (err) {
      alert("Failed to submit reply");
    } finally {
      setReplyLoading(false);
    }
  };

  const handleResolveTicket = async (id, status) => {
    try {
      await axios.put(`${constant.API_BASE_URL}/admin/business/support/tickets/${id}/status`, { status }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      await axios.post(`${constant.API_BASE_URL}/admin/business/support/tickets/${id}/reply`, {
        message: `[Admin Alert] Status marked as: ${status}`
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      fetchTickets();
    } catch (err) {
      console.error(err);
      alert("Failed to update status");
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div>
        <h2 className="text-3xl font-extrabold text-slate-800">Ticket & Support Center</h2>
        <p className="text-slate-500 mt-1">Review tickets raised by users, sellers, or agency owners and provide real-time responses.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Ticket List Column */}
        <div className="lg:col-span-1 bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/30 overflow-hidden flex flex-col max-h-[600px]">
          <div className="p-5 border-b border-slate-50 flex items-center justify-between">
            <h3 className="font-bold text-slate-800">Support Tickets</h3>
            <button onClick={fetchTickets} className="text-xs text-pink-600 font-bold hover:underline">Refresh</button>
          </div>

          {loading ? (
            <div className="p-10 text-center flex-1">
              <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-slate-500 text-xs">Loading queue...</p>
            </div>
          ) : tickets.length === 0 ? (
            <div className="p-10 text-center text-slate-400 flex-1">
              <MessageSquare size={36} className="mx-auto mb-2 opacity-35" />
              <p className="text-sm">No active tickets.</p>
            </div>
          ) : (
            <div className="overflow-y-auto flex-1 divide-y divide-slate-50">
              {tickets.map((t) => (
                <div 
                  key={t.id} 
                  onClick={() => setSelectedTicket(t)}
                  className={`p-5 cursor-pointer hover:bg-slate-50/80 transition relative ${
                    selectedTicket?.id === t.id ? 'bg-pink-50/50 border-r-4 border-pink-500' : ''
                  }`}
                >
                  <div className="flex justify-between items-start gap-2">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{t.user_type}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                      t.status === 'open' ? 'bg-red-50 text-red-600 border border-red-100' :
                      t.status === 'pending' ? 'bg-yellow-50 text-yellow-600 border border-yellow-100' : 'bg-green-50 text-green-600 border border-green-100'
                    }`}>
                      {t.status}
                    </span>
                  </div>
                  <h4 className="font-bold text-slate-800 text-sm mt-2 line-clamp-1">{t.subject}</h4>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{t.message}</p>
                  <div className="flex justify-between items-center mt-3 text-[10px] text-slate-400">
                    <span>{t.first_name} {t.last_name}</span>
                    <span>{new Date(t.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Chat / Ticket Reply Panel */}
        <div className="lg:col-span-2 flex flex-col bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/30 overflow-hidden h-[600px]">
          {selectedTicket ? (
            <>
              {/* Header */}
              <div className="p-6 border-b border-slate-100 flex items-center justify-between shrink-0 bg-white">
                <div>
                  <h3 className="font-bold text-slate-800 text-base">{selectedTicket.subject}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Raised by: {selectedTicket.first_name} {selectedTicket.last_name} ({selectedTicket.email})</p>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => handleResolveTicket(selectedTicket.id, 'solved')}
                    className="px-3.5 py-2 bg-green-500 hover:bg-green-600 active:scale-95 text-white font-bold rounded-xl text-xs transition"
                  >
                    Solve Ticket
                  </button>
                  <button 
                    onClick={() => setSelectedTicket(null)} 
                    className="p-1 text-slate-400 hover:bg-slate-100 rounded-lg"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Thread Chat Pane */}
              <div className="flex-1 p-6 overflow-y-auto bg-slate-50 space-y-6">
                {/* Original Message */}
                <div className="flex flex-col items-start max-w-[80%]">
                  <div className="bg-white p-4 rounded-2xl rounded-tl-none shadow-sm border border-slate-100">
                    <p className="text-sm text-slate-800 font-semibold">{selectedTicket.message}</p>
                    <span className="text-[10px] text-slate-400 mt-2 block">{new Date(selectedTicket.created_at).toLocaleString()}</span>
                  </div>
                </div>

                {/* Reply Threads */}
                {selectedTicket.replies && selectedTicket.replies.map((reply, i) => {
                  const isAdmin = reply.sender_name === 'Admin';
                  return (
                    <div key={i} className={`flex flex-col max-w-[80%] ${isAdmin ? 'ml-auto items-end' : 'items-start'}`}>
                      <div className={`p-4 rounded-2xl shadow-sm border ${
                        isAdmin ? 'bg-slate-900 text-white border-slate-900 rounded-tr-none' : 'bg-white text-slate-800 border-slate-100 rounded-tl-none'
                      }`}>
                        <p className="text-sm font-semibold">{reply.message}</p>
                        <span className={`text-[10px] mt-2 block ${isAdmin ? 'text-slate-400' : 'text-slate-400'}`}>
                          {reply.sender_name} • {new Date(reply.timestamp).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Input Footer */}
              <form onSubmit={handleSendReply} className="p-4 border-t border-slate-100 flex gap-3 shrink-0 bg-white">
                <input
                  required
                  placeholder="Type reply message here..."
                  className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition text-sm font-medium"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={replyLoading}
                  className="px-5 py-3 bg-pink-600 hover:bg-pink-700 active:scale-95 text-white font-bold rounded-xl transition flex items-center justify-center gap-2 shadow-lg shadow-pink-200 disabled:opacity-50"
                >
                  <Send size={16} /> Send
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center text-slate-400 p-8">
              <MessageSquare size={54} className="opacity-20 mb-3" />
              <h4 className="font-extrabold text-slate-600 text-lg">No Ticket Selected</h4>
              <p className="text-sm mt-1 max-w-sm">Select a ticket from the left panel to review files, thread details, and submit answers.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportCenter;
